import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle, Key, Copy, HelpCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import ThemeToggle from '../common/ThemeToggle';

const ForgotPasswordForm = () => {
  const navigate = useNavigate();
  const { forgotPassword, generateTempPassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [showTempOption, setShowTempOption] = useState(false);
  const [documentId, setDocumentId] = useState('');
  const [tempPassword, setTempPassword] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(email);
      // Navigate to success page with email in state
      navigate('/forgot-password/success', { state: { email } });
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTempPassword = async (e) => {
    e.preventDefault();
    if (!email || !documentId) {
      setError('Please enter your email and Student/Staff ID to verify your identity.');
      return;
    }

    setError('');
    setLoading(true);
    setTempPassword('');

    try {
      const pwd = await generateTempPassword(email, documentId);
      setTempPassword(pwd);
    } catch (err) {
      setError(err.message || 'Failed to generate temporary password.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(tempPassword);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/USIU-Library.jpg"
          alt="USIU-Africa Library"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-white/95 via-white/90 to-usiu-blue-50/95 dark:from-slate-900/95 dark:via-slate-800/90 dark:to-slate-900/95"></div>
      </div>

      {/* Theme Toggle */}
      <div className="absolute top-6 right-6 z-20">
        <ThemeToggle />
      </div>

      {/* Form Container */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <div className="max-w-md w-full space-y-8 bg-white/40 dark:bg-slate-900/40 backdrop-blur-md p-8 rounded-3xl border border-white/20 dark:border-slate-700/30 shadow-2xl">
          <div>
            <h2 className="text-center text-3xl font-extrabold bg-gradient-to-r from-gray-900 to-gray-800 dark:from-usiu-gold-400 dark:to-usiu-gold-500 bg-clip-text text-transparent">
              {showTempOption ? 'Temporary Access' : 'Recover Your Account'}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
              {showTempOption
                ? 'Verify your identity to receive an instant access code.'
                : 'Enter your official USIU email to receive a secure reset link.'}
            </p>
          </div>

          {error && (
            <div className="rounded-xl bg-red-50 dark:bg-red-900/30 border-2 border-red-200 dark:border-red-800 p-5 shadow-sm">
              <div className="flex">
                <AlertCircle className="h-6 w-6 text-red-500 shrink-0 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                    Action Required
                  </h3>
                  <div className="mt-1 text-sm text-red-700 dark:text-red-400">
                    <p>{error}</p>
                    {error.includes('mail server') && !showTempOption && (
                      <div className="mt-3">
                        <button
                          onClick={() => { setShowTempOption(true); setError(''); }}
                          className="text-usiu-blue-600 dark:text-usiu-gold-400 font-bold hover:underline"
                        >
                          Try generating a temporary password instead →
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tempPassword && (
            <div className="rounded-xl bg-green-50 dark:bg-green-900/30 border-2 border-green-200 dark:border-green-800 p-6 shadow-lg animate-in fade-in zoom-in duration-300">
              <div className="text-center space-y-4">
                <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-800/50 mb-2">
                  <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-lg font-bold text-green-900 dark:text-green-100">
                  Temporary Password Generated
                </h3>
                <div className="relative group">
                  <div className="flex items-center justify-between bg-white dark:bg-slate-800 px-4 py-3 rounded-xl border-2 border-green-200 dark:border-green-700 font-mono text-xl tracking-widest text-usiu-blue-600 dark:text-usiu-gold-400 shadow-inner">
                    <span>{tempPassword}</span>
                    <button
                      onClick={copyToClipboard}
                      className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      {copySuccess ? <CheckCircle className="h-5 w-5 text-green-500" /> : <Copy className="h-5 w-5 text-gray-400" />}
                    </button>
                  </div>
                </div>
                <div className="text-xs text-green-800 dark:text-green-300 space-y-2">
                  <p className="font-semibold uppercase">⚠️ Important Instructions:</p>
                  <ul className="text-left list-disc pl-4 space-y-1">
                    <li>Copy this password now (it will hide if you refresh).</li>
                    <li>Login immediately at the login page.</li>
                    <li>Go to <strong>Profile &gt; Security Settings</strong> to change it to a permanent one.</li>
                  </ul>
                </div>
                <Link
                  to="/login"
                  className="block w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors shadow-md"
                >
                  Proceed to Login
                </Link>
              </div>
            </div>
          )}

          {!tempPassword && (
            <form className="mt-8 space-y-6" onSubmit={showTempOption ? handleTempPassword : handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    University Email
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      className="appearance-none rounded-xl relative block w-full px-3 py-2.5 pl-10 border border-gray-300 dark:border-gray-700 dark:bg-slate-900 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-usiu-blue-400 focus:border-usiu-blue-400 sm:text-sm"
                      placeholder="e.g. jdoe@usiu.ac.ke"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                {showTempOption && (
                  <div className="animate-in slide-in-from-top-2 duration-300">
                    <label htmlFor="documentId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Student or Staff ID
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Key className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                      </div>
                      <input
                        id="documentId"
                        name="documentId"
                        type="text"
                        required
                        className="appearance-none rounded-xl relative block w-full px-3 py-2.5 pl-10 border border-gray-300 dark:border-gray-700 dark:bg-slate-900 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white focus:outline-none focus:ring-usiu-blue-400 focus:border-usiu-blue-400 sm:text-sm"
                        placeholder="e.g. 654321"
                        value={documentId}
                        onChange={(e) => setDocumentId(e.target.value)}
                      />
                    </div>
                    <p className="mt-2 text-[10px] text-gray-500 dark:text-gray-400 italic">
                      Verify your identity using your official university identification number.
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-bold rounded-xl text-white transition-all shadow-lg ${showTempOption
                    ? 'bg-gradient-to-r from-usiu-gold-500 to-usiu-gold-600 hover:from-usiu-gold-600 hover:to-usiu-gold-700'
                    : 'bg-usiu-blue-600 hover:bg-usiu-blue-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processing...
                    </div>
                  ) : (
                    showTempOption ? 'Verify and Generate Access Code' : 'Send Reset Link'
                  )}
                </button>

                {!showTempOption && (
                  <button
                    type="button"
                    onClick={() => setShowTempOption(true)}
                    className="w-full flex items-center justify-center text-sm font-medium text-usiu-blue-600 dark:text-usiu-gold-400 hover:underline"
                  >
                    <HelpCircle className="h-4 w-4 mr-1.5" />
                    Mail server issues? Get a temporary password
                  </button>
                )}

                {showTempOption && (
                  <button
                    type="button"
                    onClick={() => setShowTempOption(false)}
                    className="w-full text-center text-xs text-gray-500 dark:text-gray-400 hover:underline"
                  >
                    Back to standard email reset
                  </button>
                )}
              </div>

              <div className="flex items-center justify-center pt-2">
                <Link
                  to="/login"
                  className="flex items-center text-sm font-medium text-gray-600 hover:text-usiu-blue-600 dark:text-gray-400 dark:hover:text-usiu-gold-400"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back to login
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
